<template>
  <v-form ref="collectionEditForm" v-model="isFormValid">
    <!-- Back Button -->
    <div class="back-btn" @click="navigateToIndex">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M36 27H23.83L29.42 21.41L28 20L20 28L28 36L29.41 34.59L23.83 29H36V27Z" fill="#8C959D"/>
      </svg>
    </div>

    <v-row>
      <!-- Page Title -->
      <v-col cols="12">
        <h1>{{ pageTitle }}</h1>
      </v-col>

      <!-- Form Content -->
      <v-col cols="12" md="8">
        <!-- Language Tabs -->
        <v-tabs v-model="activeLanguageTab">
          <v-tab>RU</v-tab>
          <v-tab>UA</v-tab>
        </v-tabs>

        <!-- Language Tab Items -->
        <v-tabs-items v-model="activeLanguageTab">
          <!-- Russian Tab -->
          <v-tab-item :eager="true">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.name"
                    label="Название"
                    :rules="nameRuRules"
                    required
                    outlined
                    @input="clearFieldError('name')"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.slug"
                    label="Slug (URL)"
                    outlined
                    disabled
                    hint="Редагується тільки в RU вкладці"
                />

              </v-col>

              <v-col cols="12">
                <v-textarea
                    v-model="collection.text"
                    label="Описание"
                    outlined
                    rows="4"
                />
              </v-col>

              <!-- SEO Fields RU -->
              <v-col cols="12">
                <v-divider class="mb-4" />
                <h3 class="mb-4">SEO поля</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.seo_title"
                    label="SEO Title"
                    outlined
                    counter="60"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.seo_h1"
                    label="SEO H1"
                    outlined
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                    v-model="collection.seo_description"
                    label="SEO Description"
                    outlined
                    rows="3"
                    counter="160"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Ukrainian Tab -->
          <v-tab-item :eager="true">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.name_ua"
                    label="Назва"
                    :rules="nameUkRules"
                    required
                    outlined
                    @input="clearFieldError('name_ua')"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.slug"
                    label="Slug (URL)"
                    :rules="slugRules"
                    required
                    outlined
                    hint="Используется в адресной строке"
                    @input="clearFieldError('slug')"
                >
                  <template #append-outer>
                    <v-btn
                        small
                        text
                        color="primary"
                        @click="generateSlug"
                    >
                      Сгенерировать
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                    v-model="collection.text_ua"
                    label="Опис"
                    outlined
                    rows="4"
                />
              </v-col>

              <!-- SEO Fields UK -->
              <v-col cols="12">
                <v-divider class="mb-4" />
                <h3 class="mb-4">SEO поля</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.seo_title_ua"
                    label="SEO Title"
                    outlined
                    counter="60"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                    v-model="collection.seo_h1_ua"
                    label="SEO H1"
                    outlined
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                    v-model="collection.seo_description_ua"
                    label="SEO Description"
                    outlined
                    rows="3"
                    counter="160"
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>

        <v-divider class="my-6" />

        <!-- General Settings -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-4">Общие настройки</h3>
          </v-col>

          <v-col cols="12" md="6">
            <!-- Image Upload -->
            <v-file-input
                v-model="imageFile"
                label="Изображение подборки"
                accept="image/*"
                prepend-icon="mdi-camera"
                outlined
                @change="handleImageUpload"
            />

            <!-- Image Preview -->
            <div v-if="imagePreview || collection.image" class="image-preview mb-4">
              <v-img
                  :src="imagePreview || collection.image"
                  max-width="300"
                  max-height="200"
                  contain
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5" />
                  </v-row>
                </template>
              </v-img>
              <v-btn
                  v-if="imagePreview || collection.image"
                  small
                  color="error"
                  class="mt-2"
                  @click="removeImage"
              >
                Удалить изображение
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-switch
                v-model="collection.is_active"
                label="Активная подборка"
                color="primary"
            />

            <v-text-field v-if="collection.id"
                v-model.number="collection.sort_order"
                label="Порядок сортировки"
                type="number"
                outlined
                hint="Чем меньше число, тем выше в списке"
            />
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <!-- Products Section -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Товары в подборке</h3>
              <v-btn
                  color="primary"
                  @click="openProductPicker"
              >
                <v-icon left>mdi-plus</v-icon>
                Добавить товары
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12">
            <draggable
                v-model="collection.products"
                handle=".drag-handle"
                @end="updateProductsOrder"
            >
              <v-card
                  v-for="(product, index) in collection.products"
                  :key="product.id"
                  class="mb-2"
                  outlined
              >
                <v-card-text class="d-flex align-center">
                  <v-icon class="drag-handle mr-4" style="cursor: move">
                    mdi-drag-vertical
                  </v-icon>

                  <v-img
                      v-if="product.image"
                      :src="product.image"
                      max-width="60"
                      max-height="60"
                      class="mr-4"
                  />

                  <div class="flex-grow-1">
                    <div class="font-weight-bold">{{ product.name }}</div>
                    <div class="text-caption grey--text">
                      Артикул: {{ product.code || 'N/A' }}
                    </div>
                  </div>

                  <div class="mr-4">
                    <v-chip small>{{ product.price }} UAH</v-chip>
                  </div>

                  <v-btn
                      icon
                      color="error"
                      @click="removeProduct(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-card-text>
              </v-card>
            </draggable>

            <v-alert
                v-if="!collection.products || collection.products.length === 0"
                type="info"
                text
                class="mt-4"
            >
              Товары не добавлены. Нажмите "Добавить товары" для выбора.
            </v-alert>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <!-- Action Buttons -->
        <v-row>
          <v-col>
            <v-btn
                :loading="isButtonLoading"
                :disabled="!isFormValid || isButtonLoading"
                color="primary"
                large
                @click="handleSubmit"
            >
              {{ submitButtonText }}
            </v-btn>

            <v-btn
                text
                large
                class="ml-2"
                @click="navigateToIndex"
            >
              Отмена
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Product Picker Dialog -->
    <v-dialog v-model="productPickerDialog" max-width="900">
      <v-card>
        <v-card-title>
          Выбор товаров
          <v-spacer />
          <v-text-field
              v-model="productSearch"
              append-icon="mdi-magnify"
              label="Поиск"
              single-line
              hide-details
              class="mt-0 pt-0 mr-4"
              placeholder="Начните вводить название товара..."
              clearable
          />
          <v-checkbox
              v-model="showHiddenProducts"
              label="Показать скрытые"
              hide-details
              class="mt-0 pt-0"
          />
        </v-card-title>

        <v-card-text style="max-height: 500px; overflow-y: auto">
          <!-- Индикатор загрузки -->
          <div v-if="loadingProducts && productCategories.length === 0" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-4 grey--text">Загрузка категорий...</div>
          </div>

          <!-- Список категорий с ленивой загрузкой товаров -->
          <div v-else>
            <div v-if="filteredCategories.length === 0" class="text-center py-8 grey--text">
              Категории не найдены
            </div>
            
            <v-expansion-panels v-model="expandedPanels" multiple class="mb-4">
              <v-expansion-panel
                  v-for="category in filteredCategories"
                  :key="category.id"
                  @change="onCategoryExpand(category)"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ category.name }}</span>
                    <v-chip
                        v-if="category.products && category.products.length > 0"
                        x-small
                        class="ml-2"
                        color="primary"
                        outlined
                    >
                      {{ category.products.length }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Индикатор загрузки товаров в категории -->
                  <div v-if="category.loading" class="text-center py-4">
                    <v-progress-circular indeterminate size="24" color="primary" />
                  </div>

                  <v-data-table
                      v-else-if="category.products"
                      v-model="selectedProducts"
                      :headers="productHeaders"
                      :items="displayProducts[category.id] || []"
                      show-select
                      item-key="id"
                      hide-default-footer
                      disable-pagination
                      class="elevation-0"
                  >
                    <template #item.image="{ item }">
                      <v-img
                          v-if="item.main_image && item.main_image.url_for_list"
                          :src="item.main_image.url_for_list"
                          max-width="40"
                          max-height="40"
                          class="my-1"
                      />
                    </template>

                    <template #item.price="{ item }">
                      {{ item.price }} UAH
                    </template>
                  </v-data-table>
                  <div v-else class="text-center py-4 grey--text">
                    Нет товаров
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeProductPicker">
            Отмена
          </v-btn>
          <v-btn
              color="primary"
              @click="addSelectedProducts"
              :disabled="selectedProducts.length === 0"
          >
            Добавить ({{ selectedProducts.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
        v-model="notification.show"
        :timeout="notification.timeout"
        :color="notification.color"
        right
        top
    >
      {{ notification.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            v-bind="attrs"
            text
            @click="notification.show = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>

<script>
import CollectionService from '@/api/CollectionService'
import CategoryService from '@/api/CategoryService'
import ProductService from '@/api/ProductService'
import draggable from 'vuedraggable'
import slugify from '@/utils/slugify'

const NOTIFICATION_TIMEOUT = 2000
const VALIDATION_MESSAGES = {
  REQUIRED: 'Нужно заполнить'
}
const NOTIFICATION_MESSAGES = {
  UPDATE_SUCCESS: 'Подборка успешно обновлена',
  UPDATE_ERROR: 'Ошибка при обновлении',
  CREATE_SUCCESS: 'Подборка создана',
  CREATE_ERROR: 'Ошибка при создании'
}

export default {
  name: 'CollectionEdit',

  components: {
    draggable
  },

  props: {
    id: {
      type: [String, Number],
      default: null,
    }
  },

  data() {
    return {
      // Form state
      isFormValid: true,
      isButtonLoading: false,
      activeLanguageTab: 0,

      // Image handling
      imageFile: null,
      imagePreview: null,
      imageToDelete: false,

      // Product picker
      productPickerDialog: false,
      productSearch: '',
      expandedPanels: [],
      availableProducts: [],
      selectedProducts: [],
      showHiddenProducts: false,
      loadingProducts: false,
      productCategories: [],
      searchTimeout: null,
      productHeaders: [
        { text: 'Фото', value: 'image', sortable: false },
        { text: 'Название', value: 'name', sortable: true },
        { text: 'Цена', value: 'price', sortable: true }
      ],

      // Notification state
      notification: {
        show: false,
        text: '',
        color: ''
      },
      NOTIFICATION_TIMEOUT,

      // Validation errors from server
      validationErrors: {},

      // Validation rules
      validationRules: {
        required: [value => !!value || VALIDATION_MESSAGES.REQUIRED]
      },

      // Main data object
      collection: this.getInitialCollection()
    }
  },

  computed: {
    isEditMode() {
      return !!this.id
    },

    pageTitle() {
      return this.isEditMode
          ? 'Редактирование подборки'
          : 'Создание подборки'
    },

    submitButtonText() {
      return this.isEditMode ? 'Сохранить' : 'Создать'
    },

    // Dynamic validation rules that include server errors
    slugRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.slug) {
        rules.push(() => this.validationErrors.slug?.[0] || true)
      }
      return rules
    },

    nameRuRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.name) {
        rules.push(() => this.validationErrors.name?.[0] || true)
      }
      return rules
    },

    filteredCategories() {
      const search = this.productSearch ? this.productSearch.toLowerCase() : ''
      
      return this.productCategories.filter(category => {
        // Если поиска нет и показываем все, то категория подходит
        if (!search && this.showHiddenProducts) return true

        // Если товаров нет, фильтруем только по названию категории
        if (!category.products) {
          return !search || category.name.toLowerCase().includes(search)
        }

        // Проверяем наличие подходящих товаров
        const hasVisibleProducts = category.products.some(product => {
          const matchesSearch = !search || product.name.toLowerCase().includes(search)
          const matchesHidden = this.showHiddenProducts || product.hide === 0
          return matchesSearch && matchesHidden
        })

        return hasVisibleProducts || (search && category.name.toLowerCase().includes(search))
      })
    },

    displayProducts() {
      const search = this.productSearch ? this.productSearch.toLowerCase() : ''
      const result = {}
      
      this.filteredCategories.forEach(category => {
        if (!category.products) return
        
        result[category.id] = category.products.filter(product => {
          const matchesSearch = !search || product.name.toLowerCase().includes(search)
          const matchesHidden = this.showHiddenProducts || product.hide === 0
          return matchesSearch && matchesHidden
        })
      })
      
      return result
    },

    nameUkRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.name_ua) {
        rules.push(() => this.validationErrors.name?.[0] || true)
      }
      return rules
    }
  },


  mounted() {
    if (this.isEditMode) {
      this.loadCollection()
    }
  },

  methods: {
    getInitialCollection() {
      return {
        name: '',
        name_ua: '',
        slug: '',
        image: null,
        text: '',
        text_ua: '',
        seo_title: '',
        seo_title_ua: '',
        seo_description: '',
        seo_description_ua: '',
        seo_h1: '',
        seo_h1_ua: '',
        is_active: true,
        sort_order: 0,
        products: []
      }
    },

    async loadCollection() {
      try {
        const response = await CollectionService.get(this.id)
        this.collection = {
          ...response.data,
          products: response.data.products || []
        }
      } catch (error) {
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_ERROR, 'error')
      }
    },

    async loadAvailableProducts() {
      if (this.productCategories.length > 0) return

      this.loadingProducts = true

      try {
        const response = await CategoryService.list()
        this.productCategories = response.data.map(cat => ({
          ...cat,
          products: null,
          loading: false
        }))
      } catch (error) {
        console.error('Error loading categories:', error)
        this.productCategories = []
      } finally {
        this.loadingProducts = false
      }
    },

    async loadProductsForCategory(category) {
      if (category.products || category.loading) return

      this.$set(category, 'loading', true)

      try {
        const params = {
          page: 1,
          perPage: 500,
          categories: [category.id],
          withChildren: true
        }
        const response = await ProductService.list(params)
        this.$set(category, 'products', response.data.data)
      } catch (error) {
        console.error(`Error loading products for category ${category.id}:`, error)
      } finally {
        this.$set(category, 'loading', false)
      }
    },

    onCategoryExpand(category) {
      this.loadProductsForCategory(category)
    },

    generateSlug() {
      if (!this.collection.name_ua) {
        this.showNotification('Сначала введите название (UA)', 'warning')
        this.activeLanguageTab = 0
        return
      }

      const slug = slugify(this.collection.name_ua)

      this.collection.slug = slug
      this.clearFieldError('slug')
    },


    navigateToIndex() {
      this.$router.push({ name: 'collection-index' })
    },

    // Image handling
    handleImageUpload(file) {
      if (file) {
        this.imagePreview = URL.createObjectURL(file)
        this.imageToDelete = false
      }
    },

    removeImage() {
      this.imageFile = null
      this.imagePreview = null
      this.imageToDelete = true
      this.collection.image = null
    },

    // Product management
    openProductPicker() {
      this.selectedProducts = [...this.collection.products]
      this.productSearch = ''
      this.expandedPanels = []
      this.productPickerDialog = true
      this.loadAvailableProducts()
    },

    closeProductPicker() {
      this.productPickerDialog = false
      this.productSearch = ''
      this.selectedProducts = []
    },

    addSelectedProducts() {
      this.collection.products = [...this.selectedProducts]
      this.closeProductPicker()
      this.updateProductsOrder()
    },

    removeProduct(index) {
      this.collection.products.splice(index, 1)
      this.updateProductsOrder()
    },

    updateProductsOrder() {
      this.collection.products = this.collection.products.map((product, index) => ({
        ...product,
        sort_order: index
      }))
    },

    // Form validation and submission
    validateForm() {
      this.$refs.collectionEditForm.validate()
      this.switchToRequiredLanguageTab()
    },

    clearFieldError(fieldName) {
      if (this.validationErrors[fieldName]) {
        this.$delete(this.validationErrors, fieldName)
      }
    },

    clearAllErrors() {
      this.validationErrors = {}
    },

    handleValidationErrors(error) {
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors
        this.validationErrors = errors

        // Формуємо повідомлення про помилки
        const errorMessages = Object.keys(errors).map(field => {
          return errors[field].join(', ')
        })

        this.showNotification(
            'Ошибка валидации: ' + errorMessages.join('; '),
            'error'
        )

        // Перемикаємось на вкладку з помилкою
        if (errors.name || errors.slug) {
          this.activeLanguageTab = 0
        } else if (errors.name_ua) {
          this.activeLanguageTab = 1
        }

        // Викликаємо валідацію форми щоб показати помилки
        this.$nextTick(() => {
          this.$refs.collectionEditForm.validate()
        })
      } else {
        this.showNotification('Ошибка при сохранении', 'error')
      }
    },

    switchToRequiredLanguageTab() {
      const hasRussianName = !!this.collection.name
      const hasUkrainianName = !!this.collection.name_ua

      if (!hasRussianName && hasUkrainianName) {
        this.activeLanguageTab = 0
      } else if (hasRussianName && !hasUkrainianName) {
        this.activeLanguageTab = 1
      }
    },

    handleSubmit() {
      this.clearAllErrors()
      this.validateForm()

      if (!this.isFormValid) {
        return
      }

      if (this.isEditMode) {
        this.updateCollection()
      } else {
        this.createCollection()
      }
    },

    async updateCollection() {
      this.isButtonLoading = true

      try {
        const formData = this.getFormData()
        await CollectionService.update(this.collection.id, formData)
        this.clearAllErrors()
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_SUCCESS, 'success')
      } catch (error) {
        this.handleValidationErrors(error)
      } finally {
        this.isButtonLoading = false
      }
    },

    async createCollection() {
      this.isButtonLoading = true

      try {
        const formData = this.getFormData()
        const response = await CollectionService.create(formData)

        this.collection.id = response.data.id
        this.clearAllErrors()
        this.showNotification(NOTIFICATION_MESSAGES.CREATE_SUCCESS, 'success')

        this.$router.push({
          name: 'collection-edit',
          params: { id: response.data.data.id }
        })
      } catch (error) {
        this.handleValidationErrors(error)
      } finally {
        this.isButtonLoading = false
      }
    },

    getFormData() {
      const formData = new FormData()

      // Basic fields
      formData.append('name', this.collection.name)
      formData.append('name_ua', this.collection.name_ua)
      formData.append('slug', this.collection.slug)
      formData.append('text', this.collection.text || '')
      formData.append('text_ua', this.collection.text_ua || '')

      // SEO fields
      formData.append('seo_title', this.collection.seo_title || '')
      formData.append('seo_title_ua', this.collection.seo_title_ua || '')
      formData.append('seo_description', this.collection.seo_description || '')
      formData.append('seo_description_ua', this.collection.seo_description_ua || '')
      formData.append('seo_h1', this.collection.seo_h1 || '')
      formData.append('seo_h1_ua', this.collection.seo_h1_ua || '')

      // Settings
      formData.append('is_active', this.collection.is_active ? '1' : '0')
      formData.append('sort_order', this.collection.sort_order)

      // Image
      if (this.imageFile) {
        formData.append('image', this.imageFile)
      } else if (this.imageToDelete) {
        formData.append('remove_image', '1')
      }

      // Products
      this.collection.products.forEach((product, index) => {
        formData.append(`products[${index}][products_id]`, product.id)
        formData.append(`products[${index}][sort_order]`, index)
      })

      return formData
    },

    showNotification(text, color) {
      this.notification = {
        show: true,
        text,
        color
      }
    }
  }
}
</script>

<style scoped lang="scss">
.back-btn {
  cursor: pointer;
  display: inline-block;
  margin-bottom: 16px;

  &:hover {
    opacity: 0.7;
  }
}

.image-preview {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.drag-handle {
  cursor: move;
}
</style>